import { useState, useEffect } from "react";
import { observer, useField, useForm } from "@formily/react";
import {
  formatLicensePlate,
  validateLicensePlate,
} from "../../helpers/licensePlate";
import { Field } from "@formily/core";
import { LicensePlateInput } from "../LicensePlateInput";
import { useStateMachine } from "little-state-machine";
import { IRumbleFormScope } from ".";

interface IVehicleData {
  brand: string;
  model: string;
  buildYear: number;
}

interface ILicensePlateProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const LicensePlate = observer((props: ILicensePlateProps) => {
  const form = useForm();
  const field = useField<Field>();
  const [vehicleData, setVehicleData] = useState<IVehicleData | null>(null);
  const { state } = useStateMachine();

  // Safely extract scope and fetchData
  const scope = (state as { scope?: IRumbleFormScope }).scope;
  const fetchData = scope?.fetchData;
  if (!fetchData) {
    throw new Error("fetchData function is not available");
  }

  // Update vehicleData when form values change
  useEffect(() => {
    const brand = form.values.brand;
    const model = form.values.model;
    const buildYear = form.values.buildYear;

    if (brand && model && buildYear) {
      setVehicleData({
        brand,
        model,
        buildYear,
      });
    }
  }, [form.values.brand, form.values.model, form.values.buildYear]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = event.target.value.toUpperCase();
    const formatted = formatLicensePlate(rawInput);
    field.setValue(formatted);
    form.deleteValuesIn("brand");
    form.deleteValuesIn("model");
    form.deleteValuesIn("buildYear");

    if (!validateLicensePlate(formatted)) {
      field.setFeedback({
        type: "error",
        code: "validate",
        messages: ["Kenteken is niet geldig"],
      });
      setVehicleData(null);
      return;
    }

    try {
      if (!formatted || !(formatted.length === 8)) {
        setVehicleData(null);
        return;
      }

      const response = await fetchData({
        endpoint: `/license-plate/NL/${formatted.replace(/-/g, "")}`,
      });

      if (response?.data) {
        setVehicleData(response.data);

        form.setValues({
          brand: response.data.brand,
          model: response.data.model,
          buildYear: response.data.buildYear,
        });

        field.setFeedback({
          messages: [],
        });
      } else {
        throw new Error("Failed to fetch vehicle data");
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
      field.setFeedback({
        type: "error",
        code: "validate",
        messages: ["Kenteken niet gevonden of fout bij ophalen"],
      });
      setVehicleData(null);
    }

    // Optional: call external onChange
    if (props.onChange) {
      props.onChange(event);
    }
  };

  // Get vehicle data from API response or form values
  const getCarBrandModelYear = (): IVehicleData | null => {
    if (vehicleData) return vehicleData;

    const { brand, model, buildYear } = form.values;
    if (brand && model && buildYear) {
      return { brand, model, buildYear };
    }

    return null;
  };

  const carBrandModelYear = getCarBrandModelYear();

  return (
    <>
      <LicensePlateInput
        {...props}
        value={field.value ?? ""}
        onChange={handleChange}
        disabled={props.disabled}
      />
      {!props.disabled && (
        <div className="text-xs text-gray-500 uppercase h-2 mt-1">
          {carBrandModelYear && (
            <>
              {carBrandModelYear.brand} {carBrandModelYear.model} (
              {carBrandModelYear.buildYear})
            </>
          )}
        </div>
      )}
    </>
  );
});
